import React from 'react';
import { 
  CalendarIcon,
  HomeIcon,
  UserGroupIcon,
  TrophyIcon,
  DocumentTextIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Geschichte: React.FC = () => {
  const timeline = [
    {
      year: '1948',
      title: 'Gründung',
      description: 'Gründung der Sektion Dienst- und Gebrauchshunde in Berlin-Adlershof. Der erste Trainingsplatz wird im Birkenwäldchen eingerichtet.'
    },
    {
      year: '1952',
      title: 'Erste Infrastruktur',
      description: 'Der Hürdenparcours für die Hundeausbildung wird in Betrieb genommen. Der erste Teil des Vereinsheims entsteht durch ehrenamtliche Arbeit der Mitglieder.'
    },
    {
      year: '1953',
      title: 'Ausbau der Anlage',
      description: 'Umgestaltung des Sportheims (heutige Gaststätte) mit Unterstützung der Fernmeldebetriebe. Sanitäreinrichtungen werden gebaut.'
    },
    {
      year: '1950er-1980er',
      title: 'DDR-Zeit',
      description: 'Integration in die Betriebssportgemeinschaft (BSG) "Motor Wuhlheide". Trotz politischer Einschränkungen bleibt die Gemeinschaft stark und wird von lokalen Industriebetrieben unterstützt.'
    },
    {
      year: '20.10.1990',
      title: 'Neugründung',
      description: 'Nach der Wende erfolgt die Neugründung als "Schutz- und Gebrauchshundesportverein der SG SV e.V."'
    },
    {
      year: '11.06.1991',
      title: 'Vereinseintragung',
      description: 'Offizielle Eintragung beim Amtsgericht als eingetragener Verein.'
    },
    {
      year: '02.05.1992',
      title: 'Namensänderung',
      description: 'Umbenennung in "Hundesportverein Berlin-Adlershof e.V." (HSV-Adlershof)'
    },
    {
      year: '01.01.1993',
      title: 'DVG-Mitgliedschaft',
      description: 'Beitritt zum Deutschen Verband der Gebrauchshundsportvereine e.V. (DVG) - ein wichtiger Schritt für die überregionale Anerkennung.'
    },
    {
      year: '11.05.2006',
      title: 'Senatsverwaltung',
      description: 'Anerkennung der Erziehungstätigkeit durch die Senatsverwaltung für Bildung, Jugend und Sport - Bestätigung der pädagogischen Qualität.'
    },
    {
      year: '1990-2010',
      title: 'Moderne Entwicklung',
      description: 'Kontinuierliche Modernisierung der Trainingsmethoden und Erweiterung des Sportangebots.'
    },
    {
      year: 'Heute',
      title: 'Gegenwart',
      description: 'Mit über 75 Jahren Erfahrung ist der HSV-Adlershof eine feste Größe in der Berliner Hundesportszene mit modernen Trainingsanlagen und vielfältigen Sportangeboten.'
    }
  ];

  const personalities = [
    {
      name: 'Werner Müller',
      role: 'Gründungsmitglied',
      description: 'Ein Pionier des Vereins, der maßgeblich beim Aufbau der ersten Infrastruktur half und unzählige Stunden ehrenamtlicher Arbeit investierte.'
    },
    {
      name: 'Helmut Schwert',
      role: 'Erster Platzwart',
      description: 'Entwickelte die Sportanlagen und sorgte über Jahrzehnte für deren Pflege und Instandhaltung.'
    },
    {
      name: 'Siegfried Brunn',
      role: 'Vereinsleiter',
      description: 'Führte den Verein durch die schwierige Zeit der Wiedervereinigung und legte den Grundstein für die moderne Vereinsstruktur.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO 
        title="Vereinsgeschichte"
        description="Die Geschichte des HSV Berlin-Adlershof e.V. seit 1948 - Von der Gründung der Sektion Dienst- und Gebrauchshunde bis heute. Über 75 Jahre Hundesport in Berlin."
        url="/geschichte"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Über uns', url: '/about' },
          { name: 'Geschichte', url: '/geschichte' }
        ]}
      />
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/facilities/clubhouse.jpg" 
            alt="Historisches Vereinshaus" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="max-w-2xl">
              <Link 
                to="/about" 
                className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Zurück zu Über uns
              </Link>
              <h1 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase">
                Unsere Geschichte
              </h1>
              <p className="text-xl text-white/90">
                Über 75 Jahre Tradition und Leidenschaft
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-10 md:py-16 bg-primary-50 dark:bg-primary-900/20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="max-w-4xl mx-auto text-center">
            <CalendarIcon className="w-10 h-10 md:w-12 md:h-12 text-primary-600 dark:text-primary-400 mx-auto mb-4 md:mb-6" />
            <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100">
              Von bescheidenen Anfängen zu einer starken Gemeinschaft
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Die Geschichte des Hundesportvereins Berlin-Adlershof e.V. ist eine Geschichte von Leidenschaft, 
              Durchhaltevermögen und Gemeinschaftsgeist. Was 1948 als kleine Initiative für Dienst- und 
              Gebrauchshunde begann, hat sich über die Jahrzehnte zu einem der etabliertesten Hundesportvereine 
              im Südosten Berlins entwickelt.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-neutral-900 dark:text-neutral-100">
              Meilensteine unserer Vereinsgeschichte
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
              Von der Gründung bis heute - eine Reise durch die Zeit
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-0.5 h-full bg-primary-300 dark:bg-primary-700"></div>
            
            {timeline.map((item, index) => (
              <div key={index} className={`relative flex items-start -mb-4 last:mb-0 ${ index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse' }`} style={{ zIndex: timeline.length - index }}>
                {/* Timeline dot */}
                <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-background-dark z-20"></div>
                
                {/* Content */}
                <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="card p-4 md:p-6 hover:shadow-2xl transition-all hover:scale-[1.02] bg-white dark:bg-surface-dark relative">
                    <div className="text-primary-600 dark:text-primary-400 font-bold text-lg md:text-2xl mb-1 md:mb-2">
                      {item.year}
                    </div>
                    <h3 className="text-base md:text-xl font-semibold mb-1 md:mb-2 text-neutral-900 dark:text-neutral-100">
                      {item.title}
                    </h3>
                    <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-400">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Personalities */}
      <section className="py-10 md:py-16 bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <UserGroupIcon className="w-12 h-12 text-primary-600 dark:text-primary-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Prägende Persönlichkeiten
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Menschen, die unseren Verein geprägt haben
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {personalities.map((person, index) => (
              <div key={index} className="card p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <UserGroupIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-neutral-900 dark:text-neutral-100">
                  {person.name}
                </h3>
                <p className="text-primary-600 dark:text-primary-400 font-semibold mb-3">
                  {person.role}
                </p>
                <p className="text-neutral-600 dark:text-neutral-400">
                  {person.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-10 md:py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center">
            <TrophyIcon className="w-12 h-12 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Erfolge und Traditionen
            </h2>
            <div className="max-w-3xl mx-auto space-y-4 text-lg">
              <p className="opacity-95">
                Der HSV-Adlershof hat über die Jahre viele Traditionen entwickelt, die den Verein 
                zu dem machen, was er heute ist:
              </p>
              <ul className="text-left space-y-3 opacity-95">
                <li className="flex items-start">
                  <span className="mr-3">•</span>
                  <span>Der jährliche "Kosse Pokal" - unser interner Vereinswettbewerb</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3">•</span>
                  <span>Spaßturniere für alle Mitglieder und ihre vierbeinigen Partner</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3">•</span>
                  <span>Traditionelle Ostereisuche für Kinder und Hunde</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3">•</span>
                  <span>Anerkennung durch die Berliner Senatsverwaltung als förderungswürdige Organisation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3">•</span>
                  <span>Mitgliedschaft im DVG seit 1993</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto">
            <DocumentTextIcon className="w-12 h-12 text-primary-600 dark:text-primary-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900 dark:text-neutral-100">
              Die Geschichte geht weiter
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
              Heute steht der HSV-Adlershof für moderne Trainingsmethoden, eine starke Gemeinschaft 
              und die Förderung der Mensch-Hund-Beziehung. Wir blicken mit Stolz auf unsere Geschichte 
              zurück und mit Begeisterung in die Zukunft.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/about"
                className="btn btn-secondary"
              >
                Mehr über uns erfahren
              </Link>
              <Link
                to="/kontakt"
                className="btn btn-primary"
              >
                Teil unserer Geschichte werden
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Geschichte;